import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Loader2,
  Plus,
  Copy,
  Printer,
  Ticket,
  Hash,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Promo-codes management section embedded inside PartnerAdmin's detail
 * dialog. Lists, generates, and toggles partner-discount codes.
 *
 * One code per partner is the v1 norm but the API supports multiples,
 * so we render a list.
 */
const PartnerCodesSection = ({ partnerId, partnerName, darkMode = false }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const muted = darkMode ? 'text-slate-400' : 'text-muted-foreground';
  const rowBg = darkMode ? 'bg-slate-700/40' : 'bg-muted/40';

  const fetchCodes = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partner-codes/admin`, {
        params: { partner_id: partnerId },
      });
      setCodes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch partner codes:', err);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleGenerate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/partner-codes/admin`, {
        partner_id: partnerId,
        free_months: 1,
        active: true,
      });
      toast.success('New promo code generated');
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate code');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await axios.patch(`${API}/partner-codes/admin/${code.id}`, {
        active: !code.active,
      });
      toast.success(code.active ? 'Code deactivated' : 'Code activated');
      fetchCodes();
    } catch {
      toast.error('Failed to update code');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copied`))
      .catch(() => toast.error('Copy failed — please copy manually'));
  };

  const buildRedeemUrl = (code) =>
    `${window.location.origin}/redeem/${code}`;

  const buildFlyerUrl = (code) =>
    `${window.location.origin}/partner-promo/${code}`;

  return (
    <div className="space-y-3 pt-3 border-t border-border" data-testid="partner-codes-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ticket className="w-4 h-4 text-amber-500" />
          <h4 className="text-sm font-semibold">Promo Codes</h4>
          <span className={`text-xs ${muted}`}>
            ({codes.length} active or paused)
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleGenerate}
          disabled={creating}
          data-testid="generate-partner-code-btn"
        >
          {creating ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
          ) : (
            <Plus className="w-3.5 h-3.5 mr-1" />
          )}
          Generate Code
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      ) : codes.length === 0 ? (
        <p className={`text-xs ${muted} italic py-2`}>
          No promo codes yet. Generate one to give {partnerName || 'this partner'} a
          shareable link that grants 1 free month per redemption.
        </p>
      ) : (
        <div className="space-y-2">
          {codes.map((code) => (
            <div
              key={code.id}
              className={`p-2.5 rounded-md ${rowBg} space-y-2`}
              data-testid={`partner-code-row-${code.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-bold text-sm tracking-wide">
                  {code.code}
                </span>
                <Badge
                  variant={code.active ? 'default' : 'secondary'}
                  className={`text-xs ${code.active ? 'bg-green-600' : ''}`}
                >
                  {code.active ? 'Active' : 'Paused'}
                </Badge>
                <span className={`text-xs ${muted} flex items-center gap-1 ml-auto`}>
                  <Hash className="w-3 h-3" />
                  {code.redemption_count} redeemed
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs px-2"
                  onClick={() => copyToClipboard(code.code, 'Code')}
                  data-testid={`copy-code-${code.id}`}
                >
                  <Copy className="w-3 h-3 mr-1" /> Code
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs px-2"
                  onClick={() => copyToClipboard(buildRedeemUrl(code.code), 'Redeem link')}
                  data-testid={`copy-redeem-${code.id}`}
                >
                  <Copy className="w-3 h-3 mr-1" /> Redeem URL
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs px-2"
                  onClick={() => window.open(buildFlyerUrl(code.code), '_blank')}
                  data-testid={`open-flyer-${code.id}`}
                >
                  <Printer className="w-3 h-3 mr-1" /> Flyer
                </Button>
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className={`text-xs ${muted}`}>Active</span>
                  <Switch
                    checked={code.active}
                    onCheckedChange={() => handleToggleActive(code)}
                    data-testid={`toggle-code-${code.id}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerCodesSection;
